import { Global } from '@emotion/react';
import type { FC, MutableRefObject, MouseEvent, RefObject } from 'react';
import { useCallback, useMemo, useRef } from 'react';

import Loader from 'components/molecules/Loader';
import { PPA_PACK_ARCH_TABLE } from 'constants/ppaTestIds';

import PackArchitectureTableEmptyState from './PackArchitectureTableEmptyState';
import {
    TableContainer,
    TableContent,
    TableFooter,
    TableLoaderOverlay,
} from './PackArchitectureTable.styles';
import PackArchitectureTableToolbar from './PackArchitectureTableToolbar';
import PackArchitectureVirtualizedTable from './PackArchitectureVirtualizedTable';
import { packArchitectureTableTooltipGlobalStyles } from './PackArchitectureTableEllipsisText';
import { VIRTUAL_TABLE_HEADER_HEIGHT_PX } from './PackArchitectureTableVirtualized.styles';
import { getTableCellHeightForKpiCount } from './packArchitectureTable.helpers';
import { usePackArchitectureTable } from './usePackArchitectureTable';
import { usePackArchitectureTableTracking } from './usePackArchitectureTableTracking';

type PackArchitectureTableProps = {
    tableContainerRef?: MutableRefObject<HTMLDivElement | null>;
    tableWrapperRef?: RefObject<HTMLDivElement | null>;
    isSyncingTableFromChartRef?: MutableRefObject<boolean>;
    suppressChartToTableSyncRef?: MutableRefObject<boolean>;
    tableScrollY?: number | null;
};

const PackArchitectureTable: FC<PackArchitectureTableProps> = ({
    tableContainerRef,
    tableWrapperRef: externalTableWrapperRef,
    isSyncingTableFromChartRef,
    suppressChartToTableSyncRef,
    tableScrollY,
}) => {
    const {
        tooltip,
        tableTitle,
        kpiOptions,
        selectedKpis,
        search,
        setSearch,
        submitSearch,
        toggleKpi,
        tableWrapperRef,
        visibleColumns,
        orderedSelectedKpis,
        yAxisKpiId,
        activeSegmentKey,
        selectedColumnKey,
        hoveredRowKey,
        hoveredColumnKey,
        handleCellHover,
        handleColumnHeaderHover,
        handleTableCellClick,
        matchesKpiId,
        filteredRows,
        isFiltered,
        showTable,
        isTableEmpty,
        emptyStateTitle,
        emptyStateDescription,
        footerLabel,
        kpiTickCount,
        showTableLoader,
        isAppendingPages,
        tableLoaderMinHeight,
        tableEmptyStateMinHeight,
        getRowClassName,
        handleRowClick,
        clearHighlight,
        clearCellHover,
        isChartTableRefetching,
    } = usePackArchitectureTable({
        tableWrapperRef: externalTableWrapperRef,
        isSyncingTableFromChartRef,
        suppressChartToTableSyncRef,
    });

    const tableSectionRef = useRef<HTMLDivElement | null>(null);

    const setTableContainerNode = useCallback(
        (node: HTMLDivElement | null) => {
            tableSectionRef.current = node;
            if (tableContainerRef) {
                tableContainerRef.current = node;
            }
        },
        [tableContainerRef],
    );

    const { handleSearchSubmit, handleToggleKpi } = usePackArchitectureTableTracking({
        showTable,
        tableSectionRef,
        search,
        submitSearch,
        selectedKpis,
        toggleKpi,
        isFiltered,
        showTableLoader,
        isAppendingPages,
        isChartTableRefetching,
        isTableEmpty,
        filteredRowCount: filteredRows.length,
    });
    const resolvedTableScrollY =
        typeof tableScrollY === 'number' && !Number.isNaN(tableScrollY)
            ? Math.max(tableScrollY, tableEmptyStateMinHeight)
            : tableLoaderMinHeight;

    const handleToolbarMouseDown = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            const target = event.target as HTMLElement;
            if (target.closest('.toolbar-checkboxes') || target.closest('.toolbar-search')) {
                return;
            }
            clearHighlight();
        },
        [clearHighlight],
    );

    const rowHeightPx = useMemo(() => getTableCellHeightForKpiCount(kpiTickCount), [kpiTickCount]);
    const totalTableHeight = useMemo(
        () => resolvedTableScrollY + VIRTUAL_TABLE_HEADER_HEIGHT_PX,
        [resolvedTableScrollY],
    );

    if (!showTable) return null;

    return (
        <TableContainer ref={setTableContainerNode} data-testid={PPA_PACK_ARCH_TABLE}>
            <Global styles={packArchitectureTableTooltipGlobalStyles} />
            <PackArchitectureTableToolbar
                tableTitle={tableTitle}
                tooltip={tooltip}
                kpiOptions={kpiOptions}
                selectedKpis={selectedKpis}
                search={search}
                setSearch={setSearch}
                onSearchSubmit={handleSearchSubmit}
                onToggleKpi={handleToggleKpi}
                onMouseDown={handleToolbarMouseDown}
            />

            <TableContent ref={tableWrapperRef as RefObject<HTMLDivElement>} onMouseLeave={clearCellHover}>
                {showTableLoader ? (
                    <TableLoaderOverlay $minHeight={resolvedTableScrollY}>
                        <Loader />
                    </TableLoaderOverlay>
                ) : (isTableEmpty || filteredRows.length === 0) ? (
                    <PackArchitectureTableEmptyState
                        title={emptyStateTitle}
                        description={emptyStateDescription}
                        minHeight={resolvedTableScrollY}
                    />
                ) : (
                    <PackArchitectureVirtualizedTable
                        rowHeightPx={rowHeightPx}
                        totalTableHeight={totalTableHeight}
                        isAppendingPages={isAppendingPages}
                        visibleColumns={visibleColumns}
                        filteredRows={filteredRows}
                        hoveredRowKey={hoveredRowKey}
                        hoveredColumnKey={hoveredColumnKey}
                        orderedSelectedKpis={orderedSelectedKpis}
                        activeSegmentKey={activeSegmentKey}
                        selectedColumnKey={selectedColumnKey}
                        yAxisKpiId={yAxisKpiId}
                        matchesKpiId={matchesKpiId}
                        clearHighlight={clearHighlight}
                        handleColumnHeaderHover={handleColumnHeaderHover}
                        handleCellHover={handleCellHover}
                        handleRowClick={handleRowClick}
                        handleTableCellClick={handleTableCellClick}
                        getRowClassName={getRowClassName}
                    />
                )}
            </TableContent>

            {(!isTableEmpty || isAppendingPages) && <TableFooter>{footerLabel}</TableFooter>}
        </TableContainer>
    );
};

export default PackArchitectureTable;
